// src/utils/imageUtils.js
import imageCompression from "browser-image-compression";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { storage } from "../firebaseConfig/firebase";

/**
 * Convierte una imagen a formato WebP con 90% de calidad
 * @param {File} file - Archivo de imagen original
 * @returns {Promise<File>} - Archivo convertido a WebP
 */
export const convertToWebP = async (file) => {
  try {
    // Opciones de compresión
    const options = {
      maxSizeMB: 2, // Tamaño máximo de 2MB
      useWebWorker: true,
      fileType: "image/webp",
      initialQuality: 0.9, // 90% de calidad
    };

    console.log(`Convirtiendo ${file.name} a WebP...`);
    const compressedFile = await imageCompression(file, options);

    // Crear un nuevo File con el nombre correcto y extensión .webp
    const webpFileName = file.name.replace(/\.[^.]+$/, ".webp");
    const webpFile = new File([compressedFile], webpFileName, {
      type: "image/webp",
    });

    console.log(
      `✓ Imagen convertida: ${file.size} bytes → ${webpFile.size} bytes (${Math.round((1 - webpFile.size / file.size) * 100)}% reducción)`,
    );

    return webpFile;
  } catch (error) {
    console.error("Error al convertir imagen a WebP:", error);
    throw error;
  }
};

/**
 * Sube una imagen a Firebase Storage (con conversión automática a WebP)
 * @param {File} file - Archivo de imagen
 * @param {string} path - Ruta en Storage (ej: "hero/background-1")
 * @returns {Promise<string>} - URL de descarga de la imagen subida
 */
export const uploadImage = async (file, path) => {
  try {
    // Convertir a WebP
    const webpFile = await convertToWebP(file);

    // Crear referencia en Storage
    const storageRef = ref(storage, `${path}-${Date.now()}.webp`);

    // Subir archivo
    console.log(`Subiendo ${webpFile.name} a Storage...`);
    await uploadBytes(storageRef, webpFile);

    // Obtener URL de descarga
    const downloadURL = await getDownloadURL(storageRef);
    console.log(`✓ Imagen subida exitosamente: ${downloadURL}`);

    return downloadURL;
  } catch (error) {
    console.error("Error al subir imagen:", error);
    throw error;
  }
};

/**
 * Elimina una imagen de Firebase Storage usando su URL
 * @param {string} imageUrl - URL completa de la imagen en Firebase Storage
 * @returns {Promise<void>}
 */
export const deleteImageByUrl = async (imageUrl) => {
  if (!imageUrl || !imageUrl.includes("firebasestorage.googleapis.com")) {
    console.log("URL no válida o no es de Firebase Storage, omitiendo eliminación");
    return;
  }

  try {
    // Extraer la ruta del archivo desde la URL
    const baseUrl = "https://firebasestorage.googleapis.com/v0/b/";
    const startIndex = imageUrl.indexOf(baseUrl);

    if (startIndex === -1) {
      console.log("URL no es de Firebase Storage");
      return;
    }

    // Extraer el path: formato es /o/path%2Ffile.jpg?alt=media...
    const pathStart = imageUrl.indexOf("/o/") + 3;
    const pathEnd = imageUrl.indexOf("?");
    const encodedPath = imageUrl.substring(pathStart, pathEnd);
    const decodedPath = decodeURIComponent(encodedPath);

    console.log(`Eliminando imagen de Storage: ${decodedPath}`);

    // Crear referencia y eliminar
    const imageRef = ref(storage, decodedPath);
    await deleteObject(imageRef);

    console.log(`✓ Imagen eliminada exitosamente: ${decodedPath}`);
  } catch (error) {
    // Si el archivo no existe, no es un error crítico
    if (error.code === "storage/object-not-found") {
      console.log("La imagen ya no existe en Storage");
    } else {
      console.error("Error al eliminar imagen de Storage:", error);
      throw error;
    }
  }
};

/**
 * Elimina múltiples imágenes de Firebase Storage
 * @param {string[]} imageUrls - Array de URLs de imágenes
 * @returns {Promise<void>}
 */
export const deleteMultipleImages = async (imageUrls) => {
  const deletePromises = imageUrls
    .filter((url) => url && url.trim() !== "")
    .map((url) => deleteImageByUrl(url));

  await Promise.allSettled(deletePromises);
};

/**
 * Elimina todas las imágenes de un proyecto
 * @param {Object} project - Objeto del proyecto con estructura de imágenes
 * @returns {Promise<void>}
 */
export const deleteProjectImages = async (project) => {
  const imagesToDelete = [];

  // Agregar imagen principal
  if (project.images?.main) {
    imagesToDelete.push(project.images.main);
  }

  // Agregar thumbnail
  if (project.images?.thumbnail) {
    imagesToDelete.push(project.images.thumbnail);
  }

  // Agregar galería
  if (project.images?.gallery && Array.isArray(project.images.gallery)) {
    imagesToDelete.push(...project.images.gallery);
  }

  console.log(`Eliminando ${imagesToDelete.length} imágenes del proyecto "${project.title}"`);

  await deleteMultipleImages(imagesToDelete);
};
